/**
 * DashboardLayout — the application shell.
 *
 * Decision: ADR-057 — colour carries QC status and nothing else.
 * See docs/decisions/ADR-057-colour-means-status.md
 *
 * Rebuilt to fix five things the previous shell got wrong:
 *
 *  1. Below 1024px the sidebar became an 80px icon-only rail whose labels were
 *     `hidden lg:block`, with no aria-label and no tooltip — every nav item
 *     lost its accessible name at exactly the width where it was hardest to
 *     use, and ate 21% of a 375px viewport. Narrow widths now get an
 *     off-canvas drawer; the rail is gone.
 *  2. Three <h1> elements rendered on every screen (logo, greeting, page).
 *     There is now exactly one, and it names the current page.
 *  3. The "New Pipeline" disclosure was a <div onClick> — not focusable, not
 *     operable from a keyboard, no aria-expanded. It is a <button>.
 *  4. No skip link, in front of ~20 nav stops.
 *  5. Navigation carried gradients and five categorical hues. Chrome is
 *     neutral; the active item is marked by ground, weight and a leading rule
 *     so it does not depend on hue (accessibility.md › Vision).
 */
import React, { useEffect, useId, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import {
  LayoutDashboard,
  Plus,
  Briefcase,
  BarChart3,
  Wrench,
  Shield,
  LogOut,
  Menu,
  Moon,
  Sun,
  User,
  Database,
  ChevronDown,
  Ruler,
  Package,
  Grid3x3,
  Sparkles,
  LayoutGrid,
  Key,
  BookOpen,
  PanelLeftClose,
  PanelLeftOpen,
  X,
} from 'lucide-react';
import logo from '../assets/SP3Dlogonewblue.png';

interface NavEntry {
  path: string;
  icon: React.ElementType;
  label: string;
  adminOnly?: boolean;
}

const MAIN_NAV: NavEntry[] = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/jobs', icon: Briefcase, label: 'Jobs' },
  { path: '/analytics', icon: BarChart3, label: 'Analytics' },
  { path: '/data-utilities', icon: Wrench, label: 'Data Utilities' },
];

/** Pipelines are a category, not a severity, so they are told apart by name
 *  and icon rather than by one hue each (ADR-057). */
const PIPELINE_NAV: NavEntry[] = [
  { path: '/select-pipeline', icon: LayoutGrid, label: 'All Pipelines' },
  { path: '/pipelines/dimensions', icon: Ruler, label: 'Dimensions' },
  { path: '/pipelines/material', icon: Package, label: 'Material' },
  { path: '/pipelines/part-category', icon: Grid3x3, label: 'Part Category' },
  { path: '/pipelines/generic', icon: Sparkles, label: 'Generic' },
];

const SECONDARY_NAV: NavEntry[] = [
  { path: '/profile', icon: User, label: 'Profile' },
  { path: '/docs', icon: BookOpen, label: 'Help & FAQ' },
  { path: '/admin', icon: Shield, label: 'Admin', adminOnly: true },
];

/** Longest-prefix match, so /pipelines/dimensions/123 still titles correctly. */
const PAGE_TITLES: Array<[string, string]> = [
  ['/dashboard', 'Dashboard'],
  ['/jobs', 'Jobs'],
  ['/analytics', 'Analytics'],
  ['/data-utilities', 'Data Utilities'],
  ['/select-pipeline', 'Choose a pipeline'],
  ['/pipelines/dimensions', 'Dimensions'],
  ['/pipelines/material', 'Material'],
  ['/pipelines/part-category', 'Part Category'],
  ['/pipelines/generic', 'Generic'],
  ['/profile', 'Profile'],
  ['/docs', 'Help & FAQ'],
  ['/admin', 'Admin'],
  ['/admin/users', 'Users'],
  ['/admin/organizations', 'Organisations'],
  ['/admin/activity', 'Activity'],
  ['/admin/settings', 'System Settings'],
  ['/admin/system-config', 'System Configuration'],
  ['/admin/ai-model-config', 'AI Models'],
  ['/settings', 'Settings'],
];

function pageTitle(pathname: string): string {
  const hit = PAGE_TITLES.filter(([p]) => pathname.startsWith(p)).sort(
    (a, b) => b[0].length - a[0].length,
  )[0];
  return hit ? hit[1] : 'AI QC';
}

const DashboardLayout: React.FC = () => {
  const { user, logout } = useAuth();
  const { theme: currentTheme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [railed, setRailed] = useState(false);
  const [pipelinesOpen, setPipelinesOpen] = useState(() =>
    location.pathname.startsWith('/pipelines') || location.pathname === '/select-pipeline',
  );

  const pipelinePanelId = useId();
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  const title = pageTitle(location.pathname);

  // Keep the document title in step with the view, so browser history and tab
  // switching are meaningful.
  useEffect(() => {
    document.title = `${title} · AI QC`;
  }, [title]);

  // Route change closes the drawer — otherwise it stays over the page the
  // person just navigated to.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Escape closes the drawer and returns focus to the control that opened it,
  // so keyboard focus is never stranded behind a backdrop.
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setDrawerOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const openDjangoAdmin = async () => {
    const base = import.meta.env.VITE_API_URL || 'https://ai-qc-django-backend.fly.dev';
    try {
      const token = localStorage.getItem('access_token');
      if (token) {
        await fetch(`${base}/api/v1/auth/admin-login/`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          credentials: 'include',
        });
      }
      window.open(`${base}/admin/`, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Failed to sign in to Django admin:', error);
    }
  };

  /** A nav row. When the sidebar is railed the label is visually hidden but
   *  still the accessible name, and `title` gives pointer users a tooltip —
   *  an icon-only control must never be nameless. */
  const NavRow: React.FC<{ entry: NavEntry; nested?: boolean }> = ({ entry, nested }) => (
    <NavLink
      to={entry.path}
      title={railed ? entry.label : undefined}
      className={({ isActive }) =>
        [
          'group relative flex items-center gap-3 rounded-lg transition-colors duration-150',
          nested ? 'px-3 py-2 text-sm' : 'px-3 py-2.5',
          railed ? 'justify-center' : '',
          isActive
            ? 'bg-bg-subtle text-text-primary font-medium'
            : 'text-text-secondary hover:bg-bg-hover hover:text-text-primary',
        ].join(' ')
      }
    >
      {({ isActive }) => (
        <>
          {/* Leading rule marks the current section by position as well as by
              ground, so the state does not rest on colour alone. */}
          <span
            aria-hidden="true"
            className={[
              'absolute left-0 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-full',
              isActive ? 'bg-interactive-primary' : 'bg-transparent',
            ].join(' ')}
          />
          <entry.icon className={nested ? 'h-4 w-4 shrink-0' : 'h-5 w-5 shrink-0'} aria-hidden="true" />
          <span className={railed ? 'sr-only' : 'truncate'}>{entry.label}</span>
        </>
      )}
    </NavLink>
  );

  const sidebarInner = (
    <>
      <div className={`flex items-center gap-3 border-b border-border px-4 py-4 ${railed ? 'justify-center' : ''}`}>
        <img src={logo} alt="" aria-hidden="true" className="h-8 w-8 shrink-0" />
        {!railed && (
          <div className="min-w-0">
            <p className="truncate font-semibold text-text-primary">AI QC</p>
            <p className="truncate text-xs text-text-tertiary">Quality Control</p>
          </div>
        )}
      </div>

      <nav aria-label="Main" className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
        {MAIN_NAV.map((entry) => (
          <NavRow key={entry.path} entry={entry} />
        ))}

        <div className="pt-3">
          <button
            type="button"
            onClick={() => setPipelinesOpen((v) => !v)}
            aria-expanded={pipelinesOpen}
            aria-controls={pipelinePanelId}
            title={railed ? 'New Pipeline' : undefined}
            className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-text-secondary transition-colors duration-150 hover:bg-bg-hover hover:text-text-primary ${
              railed ? 'justify-center' : 'justify-between'
            }`}
          >
            <span className="flex items-center gap-3">
              <Plus className="h-5 w-5 shrink-0" aria-hidden="true" />
              <span className={railed ? 'sr-only' : ''}>New Pipeline</span>
            </span>
            {!railed && (
              <ChevronDown
                aria-hidden="true"
                className={`h-4 w-4 transition-transform duration-150 ${pipelinesOpen ? 'rotate-180' : ''}`}
              />
            )}
          </button>

          <div id={pipelinePanelId} hidden={!pipelinesOpen} className={`mt-1 space-y-1 ${railed ? '' : 'pl-4'}`}>
            {PIPELINE_NAV.map((entry) => (
              <NavRow key={entry.path} entry={entry} nested />
            ))}
          </div>
        </div>

        <div className="space-y-1 border-t border-border pt-3">
          {SECONDARY_NAV.filter((e) => !e.adminOnly || user?.is_superuser).map((entry) => (
            <NavRow key={entry.path} entry={entry} />
          ))}
          <NavRow entry={{ path: '/profile?tab=api', icon: Key, label: 'API Keys' }} />
        </div>
      </nav>

      <div className="space-y-1 border-t border-border p-3">
        {user?.is_superuser && (
          <button
            type="button"
            onClick={openDjangoAdmin}
            title={railed ? 'Django Admin' : undefined}
            className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-text-secondary transition-colors duration-150 hover:bg-bg-hover hover:text-text-primary ${
              railed ? 'justify-center' : ''
            }`}
          >
            <Database className="h-5 w-5 shrink-0" aria-hidden="true" />
            <span className={railed ? 'sr-only' : ''}>Django Admin</span>
          </button>
        )}

        <button
          type="button"
          onClick={handleLogout}
          title={railed ? 'Sign out' : undefined}
          className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-text-secondary transition-colors duration-150 hover:bg-bg-hover hover:text-text-primary ${
            railed ? 'justify-center' : ''
          }`}
        >
          <LogOut className="h-5 w-5 shrink-0" aria-hidden="true" />
          <span className={railed ? 'sr-only' : ''}>Sign out</span>
        </button>
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-bg-base">
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>

      {/* Desktop sidebar. Hidden below lg, where the drawer takes over. */}
      <aside
        className={`hidden shrink-0 flex-col border-r border-border bg-bg-surface lg:flex ${
          railed ? 'w-16' : 'w-64'
        }`}
      >
        {sidebarInner}
      </aside>

      {/* Narrow-width drawer. */}
      {drawerOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="absolute inset-0 bg-text-primary/40"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
          <aside
            role="dialog"
            aria-modal="true"
            aria-label="Navigation"
            className="absolute inset-y-0 left-0 flex w-72 flex-col border-r border-border bg-bg-surface shadow-lg"
          >
            <div className="flex justify-end p-2">
              <button
                type="button"
                onClick={() => {
                  setDrawerOpen(false);
                  menuButtonRef.current?.focus();
                }}
                className="rounded-lg p-2 text-text-secondary hover:bg-bg-hover hover:text-text-primary"
              >
                <X className="h-5 w-5" aria-hidden="true" />
                <span className="sr-only">Close navigation</span>
              </button>
            </div>
            {sidebarInner}
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 shrink-0 items-center gap-3 border-b border-border bg-bg-surface px-4">
          <button
            ref={menuButtonRef}
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="rounded-lg p-2 text-text-secondary hover:bg-bg-hover hover:text-text-primary lg:hidden"
          >
            <Menu className="h-5 w-5" aria-hidden="true" />
            <span className="sr-only">Open navigation</span>
          </button>

          <button
            type="button"
            onClick={() => setRailed((v) => !v)}
            className="hidden rounded-lg p-2 text-text-secondary hover:bg-bg-hover hover:text-text-primary lg:block"
          >
            {railed ? (
              <PanelLeftOpen className="h-5 w-5" aria-hidden="true" />
            ) : (
              <PanelLeftClose className="h-5 w-5" aria-hidden="true" />
            )}
            <span className="sr-only">{railed ? 'Expand sidebar' : 'Collapse sidebar'}</span>
          </button>

          {/* The one h1 on the page. It names where you are, which the previous
              greeting did not. */}
          <h1 className="min-w-0 flex-1 truncate text-base font-semibold text-text-primary">{title}</h1>

          <button
            type="button"
            onClick={toggleTheme}
            className="rounded-lg p-2 text-text-secondary hover:bg-bg-hover hover:text-text-primary"
          >
            {currentTheme === 'light' ? (
              <Moon className="h-5 w-5" aria-hidden="true" />
            ) : (
              <Sun className="h-5 w-5" aria-hidden="true" />
            )}
            <span className="sr-only">
              {currentTheme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
            </span>
          </button>

          <div className="flex items-center gap-2">
            <span className="hidden text-sm text-text-secondary sm:inline">{user?.username}</span>
            <span
              aria-hidden="true"
              className="flex h-8 w-8 items-center justify-center rounded-full bg-bg-subtle text-sm font-semibold text-text-primary"
            >
              {user?.username?.charAt(0).toUpperCase() || 'U'}
            </span>
          </div>
        </header>

        <main id="main-content" tabIndex={-1} className="flex-1 overflow-y-auto p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
